import React, { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, CheckCircle2, X, Edit2, Save, Trash2, Info, FilePlus } from 'lucide-react';

const GenericImporterDataPreviewStep = ({
    config,
    previewData,
    columnMappings,
    onImport,
    loading,
    onValidationChange,
    onDataPrepared
}) => {
    const [transformedData, setTransformedData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [editingRow, setEditingRow] = useState(null);
    const [editedValues, setEditedValues] = useState({});
    const [filteredData, setFilteredData] = useState([]);
    const [showOnlyErrors, setShowOnlyErrors] = useState(false);

    // Helper function to identify VAT fields
    const isVatField = (fieldKey, fieldConfig) => {
        return fieldKey.toLowerCase().includes('vat') ||
            fieldKey.toLowerCase().includes('moms') ||
            fieldKey === 'tax_rate' || // Added specific check for tax_rate
            (fieldConfig?.type === 'vat');
    };

    // Helper function to normalize option values to match available options
    const normalizeOptionValue = (value, fieldConfig) => {
        if (!value || !fieldConfig?.options) return value;

        const options = fieldConfig.options;
        const strValue = String(value).trim();

        // Exact match
        if (options.includes(strValue)) {
            return strValue;
        }

        // Case-insensitive match
        const lowerValue = strValue.toLowerCase();
        const caseMatch = options.find(opt => opt.toLowerCase() === lowerValue);
        if (caseMatch) {
            return caseMatch;
        }

        // Handle singular/plural variations (e.g., "Services" -> "Service")
        const singularValue = lowerValue.endsWith('s') ? lowerValue.slice(0, -1) : lowerValue;
        const pluralValue = lowerValue + 's';

        const singularMatch = options.find(opt => opt.toLowerCase() === singularValue);
        if (singularMatch) {
            return singularMatch;
        }

        const pluralMatch = options.find(opt => opt.toLowerCase() === pluralValue);
        if (pluralMatch) {
            return pluralMatch;
        }

        // Return original value if no match found (will be flagged by validation)
        return strValue;
    };

    // Helper function to reindex rows to ensure _rowIndex matches array index
    const reindexRows = (rows) => {
        return rows.map((row, index) => ({
            ...row,
            _rowIndex: index
        }));
    };

    // Transform raw data based on mappings
    useEffect(() => {
        if (previewData && columnMappings) {
            const transformed = previewData.map((row, index) => {
                const transformedRow = {};

                // Apply mappings with special handling for VAT fields and option fields
                Object.keys(columnMappings).forEach(fieldKey => {
                    const sourceColumn = columnMappings[fieldKey];
                    const fieldConfig = config.fieldsConfig[fieldKey];
                    let value = row[sourceColumn] || '';

                    // Special handling for VAT fields - normalize values
                    if (isVatField(fieldKey, fieldConfig)) {
                        // Try to normalize the VAT value
                        value = normalizeVatValue(value);
                    }

                    // Normalize option values to match available options
                    if (fieldConfig?.options && value) {
                        value = normalizeOptionValue(value, fieldConfig);
                    }

                    // Apply custom normalize function if defined
                    if (fieldConfig?.normalize && value) {
                        value = fieldConfig.normalize(value);
                    }

                    transformedRow[fieldKey] = value;
                });

                // Add row index for reference
                transformedRow._rowIndex = index;

                return transformedRow;
            });

            setTransformedData(transformed);
            setFilteredData(transformed);

            // Provide the transformed data to the parent
            if (onDataPrepared) {
                const dataForImport = transformed.map(row => {
                    const cleanRow = { ...row };
                    delete cleanRow._rowIndex;
                    return cleanRow;
                });
                onDataPrepared(dataForImport);
            }
        }
    }, [previewData, columnMappings, onDataPrepared, config.fieldsConfig]);

    // Helper function to normalize VAT values
    const normalizeVatValue = (value) => {
        if (!value && value !== 0) return '';

        // Remove any non-numeric characters except decimal point
        let numericValue = value.toString().trim();

        // If it already contains %, just extract the number
        if (numericValue.includes('%')) {
            numericValue = numericValue.replace('%', '').trim();
        }

        // Try to convert to a number
        const parsedValue = parseFloat(numericValue);

        if (isNaN(parsedValue)) {
            return value; // If parsing fails, return original value
        }

        // If the number is likely already a percentage (0-100 range)
        if (parsedValue >= 0 && parsedValue <= 100) {
            return parsedValue + '%';
        }

        // If the number is likely a decimal representation (0-1 range)
        if (parsedValue >= 0 && parsedValue <= 1) {
            return (parsedValue * 100) + '%';
        }

        // For any other case, assume it's a percentage
        return parsedValue + '%';
    };

    // Validate data
    useEffect(() => {
        if (transformedData.length > 0) {
            const errors = {};

            transformedData.forEach((row) => {
                const rowIndex = row._rowIndex;
                const rowErrors = {};

                // Validate each field
                Object.keys(config.fieldsConfig).forEach(fieldKey => {
                    const fieldConfig = config.fieldsConfig[fieldKey];
                    const value = row[fieldKey];

                    // Check if required
                    if (fieldConfig.required && (!value && value !== 0 && value !== '0' && value !== '0%')) {
                        rowErrors[fieldKey] = 'Detta fält är obligatoriskt';
                    }
                    // Run custom validation
                    else if (fieldConfig.validate && value) {
                        const validationResult = fieldConfig.validate(value);
                        if (validationResult) {
                            rowErrors[fieldKey] = validationResult;
                        }
                    }

                    // Special validation for VAT fields
                    if (isVatField(fieldKey, fieldConfig) && value) {
                        // Check if the value is properly formatted
                        if (!value.toString().includes('%')) {
                            // Try to auto-correct it
                            const correctedValue = normalizeVatValue(value);

                            // If auto-correction resulted in a change, update the value
                            if (correctedValue !== value) {
                                // Update the row data with corrected VAT value
                                setTransformedData(prevData => {
                                    return prevData.map(r => {
                                        if (r._rowIndex === rowIndex) {
                                            return { ...r, [fieldKey]: correctedValue };
                                        }
                                        return r;
                                    });
                                });
                            }
                        }
                    }
                });

                if (Object.keys(rowErrors).length > 0) {
                    errors[rowIndex] = rowErrors;
                }
            });

            setValidationErrors(errors);

            // Update filtered data if showing only rows with errors
            if (showOnlyErrors) {
                const filteredByErrors = transformedData.filter((row) =>
                    errors[row._rowIndex] !== undefined
                );
                setFilteredData(filteredByErrors);
            } else {
                setFilteredData(transformedData);
            }

            // Communicate validation state to parent
            if (onValidationChange) {
                const isValid = Object.keys(errors).length === 0 && transformedData.length > 0;
                onValidationChange(isValid);
            }
        }
    }, [transformedData, config.fieldsConfig, showOnlyErrors, onValidationChange]);

    // Count errors
    const errorCount = useMemo(() => {
        return Object.keys(validationErrors).length;
    }, [validationErrors]);

    // Start editing a row
    const handleEditRow = (rowIndex) => {
        const rowData = transformedData.find(row => row._rowIndex === rowIndex);
        setEditingRow(rowIndex);
        setEditedValues(rowData ? { ...rowData } : {});
    };

    // Save edited row
    const handleSaveRow = (rowIndex) => {
        const updatedValues = { ...editedValues };

        // Normalize VAT values before saving
        Object.keys(config.fieldsConfig).forEach(fieldKey => {
            if (isVatField(fieldKey, config.fieldsConfig[fieldKey]) && updatedValues[fieldKey]) {
                updatedValues[fieldKey] = normalizeVatValue(updatedValues[fieldKey]);
            }
        });

        const updatedData = transformedData.map(row => {
            if (row._rowIndex === rowIndex) {
                return { ...row, ...updatedValues };
            }
            return row;
        });

        setTransformedData(updatedData);
        setEditingRow(null);
        setEditedValues({});

        // Update the parent with the new prepared data
        if (onDataPrepared) {
            const dataForImport = updatedData.map(row => {
                const cleanRow = { ...row };
                delete cleanRow._rowIndex;
                return cleanRow;
            });
            onDataPrepared(dataForImport);
        }
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditedValues({});
    };

    // Delete row - Fixed to properly handle indexes
    const handleDeleteRow = (rowIndex) => {
        // Filter out the row to delete
        const filteredData = transformedData.filter(row => row._rowIndex !== rowIndex);

        // Reindex all rows to ensure _rowIndex matches array index
        const reindexedData = reindexRows(filteredData);

        // Create a mapping between old indexes and new indexes for error handling
        const indexMap = {};

        transformedData.forEach((row) => {
            if (row._rowIndex < rowIndex) {
                // Rows before the deleted one keep their index
                indexMap[row._rowIndex] = row._rowIndex;
            } else if (row._rowIndex > rowIndex) {
                // Rows after the deleted one shift up by 1
                indexMap[row._rowIndex] = row._rowIndex - 1;
            }
        });

        // Update validation errors with new indexes
        const updatedErrors = {};

        Object.keys(validationErrors).forEach(oldIndex => {
            const numOldIndex = parseInt(oldIndex);

            // Skip the deleted row's errors
            if (numOldIndex !== rowIndex && indexMap[numOldIndex] !== undefined) {
                updatedErrors[indexMap[numOldIndex]] = validationErrors[numOldIndex];
            }
        });

        setTransformedData(reindexedData);
        setValidationErrors(updatedErrors);

        // Reset editing state if we were editing a row
        if (editingRow !== null) {
            setEditingRow(null);
            setEditedValues({});
        }

        // Update filtered data
        if (showOnlyErrors) {
            const filteredByErrors = reindexedData.filter((row) =>
                updatedErrors[row._rowIndex] !== undefined
            );
            setFilteredData(filteredByErrors);
        } else {
            setFilteredData(reindexedData);
        }

        // Update the parent with the new prepared data
        if (onDataPrepared) {
            const dataForImport = reindexedData.map(row => {
                const cleanRow = { ...row };
                delete cleanRow._rowIndex;
                return cleanRow;
            });
            onDataPrepared(dataForImport);
        }
    };

    // Handle edited field change
    const handleFieldChange = (fieldKey, value) => {
        setEditedValues(prev => ({
            ...prev,
            [fieldKey]: value
        }));
    };

    // Toggle filter to show only rows with errors
    const handleToggleErrorFilter = () => {
        setShowOnlyErrors(!showOnlyErrors);

        if (!showOnlyErrors) {
            // Show only rows with errors
            const filteredByErrors = transformedData.filter((row) =>
                validationErrors[row._rowIndex] !== undefined
            );
            setFilteredData(filteredByErrors);
        } else {
            // Show all rows
            setFilteredData(transformedData);
        }
    };

    // Function to check if a field is a VAT field (for use in render)
    const checkIsVatField = (fieldKey, fieldConfig) => {
        return fieldKey.toLowerCase().includes('vat') ||
            fieldKey.toLowerCase().includes('moms') ||
            fieldKey === 'tax_rate' ||
            (fieldConfig?.type === 'vat');
    };

    return (
        <>
            <style>{`
        .preview-container {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        
        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .preview-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
        }
        
        .preview-filters {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .filter-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .filter-btn-error {
          background: ${showOnlyErrors ? '#fef2f2' : '#fff'};
          color: ${showOnlyErrors ? '#dc2626' : '#6b7280'};
          border: 1px solid ${showOnlyErrors ? '#fecaca' : '#e5e7eb'};
        }
        
        .filter-btn-error:hover {
          background: #fef2f2;
          border-color: #fecaca;
          color: #dc2626;
        }
        
        .summary-box {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          padding: 1rem 1.5rem;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          margin-bottom: 1rem;
        }
        
        .summary-stat {
          display: flex;
          flex-direction: column;
        }
        
        .summary-label {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--bs-gray);
          margin-bottom: 0.25rem;
        }
        
        .summary-value {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .summary-value-error {
          color: #dc2626;
        }
        
        .summary-value-success {
          color: #16a34a;
        }
        
        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        
        .data-table th {
          background: #f8fafc;
          text-align: left;
          padding: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
          color: #1e293b;
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        .data-table td {
          padding: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
          color: #4b5563;
        }
        
        .data-table tr:hover td {
          background-color: #f8fafc;
        }
        
        .data-table-scroll {
          max-height: 400px;
          overflow-y: auto;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          margin-bottom: 1.5rem;
        }
        
        .row-error {
          background-color: #fef2f2 !important;
        }
        
        .cell-error {
          position: relative;
        }
        
        .error-indicator {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          color: #dc2626;
        }
        
        .row-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          justify-content: flex-end;
        }
        
        .row-action-btn {
          background: white;
          border: 1px solid #e5e7eb;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .edit-btn {
          color: var(--bs-primary);
        }

        .edit-btn:hover {
          background: #f0fdf4;
          border-color: #bbf7d0;
        }
        
        .delete-btn {
          color: #dc2626;
        }
        
        .delete-btn:hover {
          background: #fef2f2;
          border-color: #fecaca;
        }
        
        .save-btn {
          color: #16a34a;
        }
        
        .save-btn:hover {
          background: #f0fdf4;
          border-color: #bbf7d0;
        }
        
        .cancel-btn {
          color: #6b7280;
        }
        
        .cancel-btn:hover {
          background: #f9fafb;
          border-color: #d1d5db;
        }
        
        .editing-cell {
          padding: 0 !important;
        }
        
        .edit-input {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 0.875rem;
        }
        
        .edit-input.error {
          border-color: #dc2626;
          background-color: #fef2f2;
        }
        
        .edit-select {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 0.875rem;
        }
        
        .alert-info {
          background-color: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 6px;
          padding: 1rem;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .alert-info-icon {
          color: var(--bs-primary);
          flex-shrink: 0;
        }

        .alert-info-content {
          flex: 1;
        }

        .alert-info-title {
          color: #166534;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .alert-info-text {
          color: #15803d;
          font-size: 0.875rem;
        }
        
        .alert-warning {
          background-color: #fff7ed;
          border: 1px solid #ffedd5;
          border-radius: 6px;
          padding: 1rem;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        
        .alert-warning-icon {
          color: #ea580c;
          flex-shrink: 0;
        }
        
        .alert-warning-content {
          flex: 1;
        }
        
        .alert-warning-title {
          color: #9a3412;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }
        
        .alert-warning-text {
          color: #c2410c;
          font-size: 0.875rem;
        }
        
        .normalized-value {
          background-color: #f0fdf4;
          border-left: 3px solid var(--bs-success);
          padding-left: 3px;
        }
      `}</style>

            <div className="preview-container">
                <div className="alert-info">
                    <Info className="alert-info-icon" size={20} />
                    <div className="alert-info-content">
                        <div className="alert-info-title">
                            Granska och justera data innan import
                        </div>
                        <div className="alert-info-text">
                            Kontrollera att datan ser korrekt ut. Du kan redigera eller ta bort rader med fel.
                            Momsvärden normaliseras automatiskt (t.ex. 25 blir 25%).
                        </div>
                    </div>
                </div>

                {errorCount > 0 && (
                    <div className="alert-warning">
                        <AlertTriangle className="alert-warning-icon" size={20} />
                        <div className="alert-warning-content">
                            <div className="alert-warning-title">
                                {errorCount} {errorCount === 1 ? 'rad har' : 'rader har'} fel som måste åtgärdas
                            </div>
                            <div className="alert-warning-text">
                                Redigera eller ta bort rader med fel innan du kan importera datan.
                            </div>
                        </div>
                    </div>
                )}

                <div className="summary-box">
                    <div className="summary-stat">
                        <span className="summary-label">Totalt antal rader</span>
                        <span className="summary-value">
                            <FilePlus size={16} />
                            {transformedData.length}
                        </span>
                    </div>

                    <div className="summary-stat">
                        <span className="summary-label">Rader med fel</span>
                        <span className={`summary-value ${errorCount > 0 ? 'summary-value-error' : ''}`}>
                            <AlertTriangle size={16} />
                            {errorCount}
                        </span>
                    </div>

                    <div className="summary-stat">
                        <span className="summary-label">Rader klara för import</span>
                        <span className={`summary-value ${errorCount === 0 ? 'summary-value-success' : ''}`}>
                            <CheckCircle2 size={16} />
                            {transformedData.length - errorCount}
                        </span>
                    </div>
                </div>

                <div className="preview-filters">
                    <button
                        className="filter-btn filter-btn-error"
                        style={{
                            background: showOnlyErrors ? '#fef2f2' : '#fff',
                            color: showOnlyErrors ? '#dc2626' : '#6b7280',
                            borderColor: showOnlyErrors ? '#fecaca' : '#e5e7eb',
                        }}
                        onClick={handleToggleErrorFilter}
                    >
                        <AlertTriangle size={14} />
                        {showOnlyErrors ? 'Visa alla rader' : 'Visa bara rader med fel'}
                    </button>
                </div>

                <div className="data-table-scroll">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>#</th>
                                {Object.keys(config.fieldsConfig).map((fieldKey) => (
                                    <th key={fieldKey}>{config.fieldsConfig[fieldKey].label}</th>
                                ))}
                                <th style={{ width: '80px' }}>Åtgärder</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length > 0 ? (
                                filteredData.map((row, visibleIndex) => {
                                    const rowIndex = row._rowIndex;
                                    const hasErrors = validationErrors[rowIndex] !== undefined;
                                    const isEditing = editingRow === rowIndex;

                                    return (
                                        <tr key={rowIndex} className={hasErrors ? 'row-error' : ''}>
                                            <td>{visibleIndex + 1}</td>

                                            {Object.keys(config.fieldsConfig).map((fieldKey) => {
                                                const fieldConfig = config.fieldsConfig[fieldKey];
                                                const fieldError = validationErrors[rowIndex]?.[fieldKey];
                                                const isFieldError = !!fieldError;
                                                const vatField = checkIsVatField(fieldKey, fieldConfig);
                                                const value = row[fieldKey];
                                                const wasNormalized = vatField &&
                                                    value &&
                                                    typeof value === 'string' &&
                                                    value.includes('%');

                                                // Render editing fields or static content
                                                return (
                                                    <td
                                                        key={fieldKey}
                                                        className={`${isFieldError ? 'cell-error' : ''} ${isEditing ? 'editing-cell' : ''}`}
                                                        title={isFieldError ? fieldError : ''}
                                                    >
                                                        {isEditing ? (
                                                            fieldConfig.options ? (
                                                                // Dropdown for fields with options
                                                                <select
                                                                    className={`edit-select ${isFieldError ? 'error' : ''}`}
                                                                    value={editedValues[fieldKey] || ''}
                                                                    onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                                                >
                                                                    <option value="">-- Välj --</option>
                                                                    {(fieldConfig.displayOptions || fieldConfig.options).map((option, index) => (
                                                                        <option key={option} value={fieldConfig.options[index]}>
                                                                            {option}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                // Text input for regular fields
                                                                <input
                                                                    type={fieldConfig.type || "text"}
                                                                    className={`edit-input ${isFieldError ? 'error' : ''}`}
                                                                    value={editedValues[fieldKey] || ''}
                                                                    onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                                                    placeholder={fieldConfig.label}
                                                                />
                                                            )
                                                        ) : (
                                                            // Static content
                                                            <>
                                                                <span className={wasNormalized ? 'normalized-value' : ''}>
                                                                    {value || '-'}
                                                                </span>
                                                                {isFieldError && (
                                                                    <AlertTriangle size={14} className="error-indicator" />
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                );
                                            })}

                                            <td>
                                                <div className="row-actions">
                                                    {isEditing ? (
                                                        <>
                                                            <button
                                                                className="row-action-btn save-btn"
                                                                onClick={() => handleSaveRow(rowIndex)}
                                                                title="Spara ändringar"
                                                            >
                                                                <Save size={14} />
                                                            </button>
                                                            <button
                                                                className="row-action-btn cancel-btn"
                                                                onClick={handleCancelEdit}
                                                                title="Avbryt"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                className="row-action-btn edit-btn"
                                                                onClick={() => handleEditRow(rowIndex)}
                                                                title="Redigera"
                                                            >
                                                                <Edit2 size={14} />
                                                            </button>
                                                            <button
                                                                className="row-action-btn delete-btn"
                                                                onClick={() => handleDeleteRow(rowIndex)}
                                                                title="Ta bort"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={Object.keys(config.fieldsConfig).length + 2} style={{ textAlign: 'center', padding: '2rem' }}>
                                        Inga rader att visa.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Error count summary */}
                <div>
                    {errorCount > 0 && (
                        <span style={{ color: '#dc2626', fontSize: '0.875rem' }}>
                            <AlertTriangle size={14} className="me-1" style={{ verticalAlign: 'middle' }} />
                            {errorCount} {errorCount === 1 ? 'fel' : 'fel'} måste åtgärdas innan import
                        </span>
                    )}
                </div>
            </div>
        </>
    );
};

export default GenericImporterDataPreviewStep;